<template>
  <page-list
    :list="list"
    :columns="showColumns"
    :group-actions="groupActions"
    :single-actions="singleActions"
    :export-data-options="exportDataOptions">
    <template v-if="!hiddenGroupBy" v-slot:group-actions-append>
      <fixed-label-filter class="ml-2" :label="$t('meter.rule_group_type')">
        <base-select
          v-model="group_by"
          :options="groupByOpts" />
      </fixed-label-filter>
    </template>
  </page-list>
</template>

<script>
import * as R from 'ramda'
// import { RuleTypes } from '@Bill/constants'
import expectStatus from '@/constants/expectStatus'
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import { getBrandFilter, getTenantFilter, getAccountFilter, getDomainFilter, getStatusFilter, getMonthFilter } from '@/utils/common/tableFilter'
import { arrToObjByKey } from '@/utils/utils'
import currencyRequest from '@Bill/mixins/currencyRequest'
import SingleActionsMixin from '../mixins/singleActions'
import ColumnsMixin from '../mixins/columns'

// const RuleTypesMap = arrayToObj(RuleTypes, 'key')

export default {
  name: 'SuggestAlertList',
  mixins: [WindowsMixin, ListMixin, ColumnsMixin, SingleActionsMixin, currencyRequest],
  props: {
    id: String,
    cloudEnv: {
      type: String,
    },
    getParams: {
      type: Object,
      default: () => ({}),
    },
    hiddenColumns: {
      type: Array,
      default: () => ([]),
    },
    hiddenGroupBy: {
      type: Boolean,
    },
  },
  data () {
    let ruleScope = 'cost'
    if (this.$route.path.startsWith('/iam')) {
      ruleScope = 'security'
    }
    const filter = {}
    let initGroupBy = 'type'
    if (this.$route.query.hasOwnProperty('status')) {
      filter.status = [this.$route.query.status]
    }
    if (this.$route.query.hasOwnProperty('month')) {
      filter.month = this.$route.query.month
    }
    if (this.$route.query.hasOwnProperty('group_by')) {
      initGroupBy = this.$route.query.group_by
    }
    return {
      ruleScope: ruleScope,
      list: this.$list.createList(this, {
        id: this.id,
        apiVersion: 'v1',
        resource: 'suggestsysalerts',
        getParams: this.getParam,
        steadyStatus: Object.values(expectStatus.suggestsysalert).flat(),
        filterOptions: {
          name: {
            label: this.$t('bill.text_72'),
            filter: true,
            formatter: val => {
              return `name.contains("${val}")`
            },
          },
          id: { label: 'ID' },
          rule_name: {
            label: this.$t('meter.rule_name'),
            dropdown: true,
            distinctField: {
              type: 'extra_field',
              key: 'rule_name',
              getParams: this.getParam,
            },
            multiple: true,
            mapper: list => {
              return list.filter(item => item.key)
            },
          },
          status: getStatusFilter({ statusModule: 'suggestsysalert', title: this.$t('meter.suggest_status') }),
          brand: getBrandFilter('compute_engine_brands'),
          projects: getTenantFilter(),
          cloudaccount: getAccountFilter(),
          project_domains: getDomainFilter(),
          usage_type: {
            label: this.$t('bill.usage_type'),
            dropdown: true,
            distinctField: {
              type: 'field',
              key: 'usage_type',
              getParams: this.getParam,
            },
            mapper: list => {
              return list.filter(item => item.key)
            },
          },
          usage_unit: {
            label: this.$t('meter.usage_unit'),
            dropdown: true,
            distinctField: {
              type: 'field',
              key: 'usage_unit',
              getParams: this.getParam,
            },
            mapper: list => {
              return list.filter(item => item.key)
            },
          },
          month: getMonthFilter(),
          host: {
            label: this.$t('dictionary.host'),
            filter: true,
            formatter: val => {
              return `host.contains("${val}")`
            },
          },
          host_ip: {
            label: this.$t('dictionary.hostip'),
            filter: true,
            formatter: val => {
              return `host_ip.contains("${val}")`
            },
          },
          ip_addr: {
            label: 'IP',
            filter: true,
            formatter: val => {
              return `ip_addr.contains("${val}")`
            },
          },
        },
        filter,
        hiddenColumns: ['name', 'type', 'account'],
      }),
      groupActions: [
        {
          label: this.$t('bill.text_269'),
          permission: 'suggestsysalerts_perform_ignore',
          action: (obj) => {
            const columnsMap = arrToObjByKey(this.columns, 'field')
            this.createDialog('IgnoreDialog', {
              vm: this,
              data: this.list.selectedItems,
              columns: [columnsMap.res_name.data, columnsMap.resource_type.data, columnsMap.rule_name.data, columnsMap.suggest_rule.data],
              onManager: this.onManager,
              refresh: this.refresh,
              success: () => {
                this.destroySidePage(this.windowId)
              },
            })
          },
          meta: (obj) => {
            const validate = this.list.selectedItems.length
            return {
              validate,
              tooltip: !validate ? this.$t('bill.subscription_validate_delete') : '',
            }
          },
        },
      ],
      group_by: initGroupBy,
      groupByOpts: [
        { id: 'type', name: this.$t('meter.rule_group_by_type') },
        { id: 'res', name: this.$t('meter.rule_group_by_res') },
      ],
    }
  },
  computed: {
    showColumns () {
      return this.columns.filter(item => {
        let ret = this.group_by === 'type' ? !['host', 'host_ip'].includes(item.field) : (!item.isResHide)
        if (this.hiddenColumns.includes(item.field)) ret = false
        return ret
      })
    },
    exportDataOptions () {
      return {
        downloadType: 'local',
        title: this.$t('bill.text_181'),
        items: [
          ...this.showColumns.filter(item => item.field !== 'res_name'),
          {
            field: 'res_id',
            title: this.$t('bill.text_120'),
          },
          {
            field: 'res_name',
            title: this.$t('bill.text_72'),
            formatter: ({ row }) => row.name,
          },
        ],
      }
    },
  },
  watch: {
    cloudEnv (val) {
      this.$nextTick(() => {
        this.list.fetchData(0)
      })
    },
    currencyParams: {
      handler: function () {
        this.list.fetchData()
      },
      deep: true,
    },
    group_by () {
      this.list.refresh()
    },
  },
  created () {
    this.initSidePageTab('suggestsalert-detail')
    this.list.fetchData()
  },
  methods: {
    getParam () {
      const ret = {
        rule_scope: this.ruleScope,
        ...(R.is(Function, this.getParams) ? this.getParams() : this.getParams),
        exchanged_currency: this.currencyParams.exchanged_currency,
      }
      if (this.group_by === 'res') {
        ret.group_by = ['res_type', 'res_id', 'month']
      }
      if (this.cloudEnv) ret.cloud_env = this.cloudEnv
      return ret
    },
    handleOpenSidepage (row) {
      this.sidePageTriggerHandle(this, 'SuggestsAlertSidePage', {
        id: row.id,
        resource: 'suggestsysalerts',
        apiVersion: 'v1',
        getParams: this.getParam,
      }, {
        list: this.list,
      })
    },
    openSuggestAlertDialog (obj) {
      this.createDialog('SuggestsAlertDeleteDialog', {
        data: [obj],
        columns: this.showColumns,
        onManager: this.onManager,
      })
    },
  },
}
</script>
